/**
 * Creates a query key builder function for a given base key array.
 *
 * The returned function accepts an optional `filters` object and returns
 * a query key array: `[...baseKey]` when no filters are provided, or
 * `[...baseKey, filters]` when filters are present.
 *
 * @param baseKey - The root array identifier for the query key.
 * @returns A function `(filters?: Partial<TFilters>) => unknown[]`
 *
 * @example
 * const OrthoNeedsQueryKey = {
 *   list: buildQueryKey<IOrthoNeedsFilter>(["patientOrtNeeds"]),
 * };
 * OrthoNeedsQueryKey.list();                   // ["patientOrtNeeds"]
 * OrthoNeedsQueryKey.list({ patient: "123" }); // ["patientOrtNeeds", { patient: "123" }]
 *
 * Filter-driven composables append the same plain segment via `useGenericQueryKey()` so
 * invalidate/refetch calls using the builder stay aligned with mounted queries.
 */
export function buildQueryKey<TFilters extends object | string | number>(baseKey: unknown[]) {
  const fn = (filters?: Partial<TFilters>): readonly any[] => [
    ...baseKey,
    ...(filters ? [filters] : []),
  ];
  fn.join = (separator = "/") => (baseKey as string[]).join(separator);
  fn.toString = () => fn.join("/");
  return fn;
}

/**
 * Creates a scoped query key builder bound to a base string.
 * The returned function accepts a variant string and optional filter type,
 * eliminating the need to repeat the base key on every entry.
 *
 * @example
 * const key = buildScopedQueryKey(MyService.queryKey);
 * const MyQueryKeys = {
 *   list:   key<IMyFilters>("list"),
 *   detail: key<string>("detail"),
 * };
 */
export function buildScopedQueryKey(base: string | string[]) {
  return <TFilters extends object | string | number>(variant: string | string[]) =>
    buildQueryKey<TFilters>([
      ...(Array.isArray(base) ? base : [base]),
      ...(Array.isArray(variant) ? variant : [variant]),
    ]);
}

type ScopedKeyFn = ReturnType<typeof buildScopedQueryKey>;

type AllKeyFn = () => readonly string[];

/**
 * Creates a query key map for an entity, injecting a scoped `key` builder so
 * the base is defined once and never repeated.
 *
 * The returned object always includes an `all` key that returns the bare base
 * array — use it to invalidate every query under this namespace at once:
 * `queryClient.invalidateQueries({ queryKey: MyQueryKeys.all() })`
 *
 * @example
 * const MyQueryKeys = createQueryKeys(MyService.queryKey, (key) => ({
 *   list:   key<IMyFilters>("list"),
 *   detail: key<string>("detail"),
 * }));
 * MyQueryKeys.all()             // ["v1", "practice"]
 * MyQueryKeys.list()            // ["v1", "practice", "list"]
 * MyQueryKeys.list({ id: "1" }) // ["v1", "practice", "list", { id: "1" }]
 */
export function createQueryKeys<T extends Record<string, ReturnType<ScopedKeyFn>>>(
  base: string | string[],
  factory: (key: ScopedKeyFn) => T
): T & { all: AllKeyFn } {
  const baseArr = Array.isArray(base) ? base : [base];
  return {
    ...factory(buildScopedQueryKey(base)),
    all: () => baseArr,
  };
}
